import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ChefHat, Search, X } from "lucide-react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import styles from "./RoleSelectorModal.module.css";

const ROLE_OPTIONS = [
  {
    id: "hire",
    title: "I need a Cook",
    subtext:
      "If you are looking for a chef, housecook, party cook or caterers",
    to: "/employer-registration",
    icon: Search,
  },
  {
    id: "cook",
    title: "I am a Cook",
    subtext:
      "If you are a chef, housecook, party cook or caterers looking for job/work",
    to: "/cook-signup",
    icon: ChefHat,
  },
];

export default function RoleSelectorModal({ open, onClose }) {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  useLockBodyScroll(open);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    closeBtnRef.current?.focus();

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className={styles.overlay} role="presentation">
      <button
        type="button"
        className={styles.backdrop}
        aria-label="Close role selector"
        onClick={onClose}
      />

      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="role-selector-title"
      >
        <button
          ref={closeBtnRef}
          type="button"
          className={styles.closeBtn}
          aria-label="Close"
          onClick={onClose}
        >
          <X size={18} />
        </button>

        <h2 id="role-selector-title" className={styles.title}>
          How do you want to get started?
        </h2>
        <p className={styles.subtitle}>
          Choose the option that best matches your goal.
        </p>

        <div className={styles.options}>
          {ROLE_OPTIONS.map(({ id, title, subtext, to, icon: Icon }) => (
            <Link
              key={id}
              to={to}
              className={styles.option}
              onClick={onClose}
            >
              <span className={styles.optionIcon} aria-hidden="true">
                <Icon size={22} />
              </span>
              <span className={styles.optionCopy}>
                <span className={styles.optionTitle}>{title}</span>
                <span className={styles.optionSubtext}>{subtext}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
